import ReportPreferences from "../components/ReportPreferences";
import MfaSettings from "../components/MfaSettings";
import { useState } from "react";
import { Loader2, User, Bell, ShieldCheck, Globe2, Network, Zap, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const NOTIF_ITEMS = [
  { key: "down", label: "Webapp DOWN", icon: ArrowDownCircle },
  { key: "recovered", label: "Webapp RECOVERED", icon: ArrowUpCircle },
  { key: "ssl", label: "SSL Expiry", icon: ShieldCheck },
  { key: "domain", label: "Domain Expiry", icon: Globe2 },
  { key: "dns", label: "DNS Changes", icon: Network },
  { key: "performance", label: "Performance", icon: Zap },
];

export default function Settings() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [savingAccount, setSavingAccount] = useState(false);
  const [notifs, setNotifs] = useState(user?.notifications || {});
  const [savingNotifs, setSavingNotifs] = useState(false);

  const saveAccount = async (e) => {
    e.preventDefault();
    setSavingAccount(true);
    try {
      const payload = { name };
      if (password) { payload.password = password; payload.current_password = currentPassword; }
      const { data } = await api.put("/auth/profile", payload);
      if (password) { setUser(false); } else { setUser(data); }
      setCurrentPassword("");
      setPassword("");
      toast.success("Account updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingAccount(false);
    }
  };

  const toggle = (key) => setNotifs((prev) => ({ ...prev, [key]: !prev[key] }));

  const saveNotifs = async () => {
    setSavingNotifs(true);
    try {
      const full = NOTIF_ITEMS.reduce((acc, it) => ({ ...acc, [it.key]: !!notifs[it.key] }), {});
      const { data } = await api.put("/auth/notifications", full);
      setUser(data);
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingNotifs(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl min-w-0 space-y-6 sm:space-y-8" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Manage your profile, login credentials and monitoring notifications.</p>
      </div>

      <div className="grid min-w-0 gap-5 lg:grid-cols-2 lg:items-start">
      <Card className="om-premium-card min-w-0 p-5 sm:p-7" data-testid="account-section">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-semibold text-slate-900"><User size={18} /> Account</h2>
        <form onSubmit={saveAccount} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" className="focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Email</Label>
            <Input value={user?.email || ""} disabled className="bg-slate-50 font-mono" data-testid="settings-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">New password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" data-testid="settings-password-input" className="focus:ring-2 focus:ring-blue-500" />
          </div>
          {password && <div className="space-y-1.5"><Label htmlFor="current-password">Current password</Label><Input id="current-password" type="password" autoComplete="current-password" required value={currentPassword} onChange={e=>setCurrentPassword(e.target.value)} /></div>}
          <Button type="submit" disabled={savingAccount} data-testid="save-account-btn" className="min-h-11 rounded-xl bg-[#536d9f] text-white hover:bg-[#435c8a]">
            {savingAccount && <Loader2 size={15} className="mr-2 animate-spin" />} Save account
          </Button>
        </form>
      </Card>

      <MfaSettings />
      <ReportPreferences />
      <Card className="om-premium-card min-w-0 p-5 sm:p-7" data-testid="notifications-section">
        <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold text-slate-900"><Bell size={18} /> Notifications</h2>
        <p className="mb-5 text-sm text-slate-500">Choose which email alerts you receive.</p>
        <div className="divide-y divide-slate-100">
          {NOTIF_ITEMS.map((it) => (
            <div key={it.key} className="flex min-w-0 items-center justify-between gap-3 py-3.5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="rounded-lg bg-slate-100 p-2 text-slate-500"><it.icon size={16} /></span>
                <span className="min-w-0 text-sm font-medium text-slate-800">{it.label}</span>
              </div>
              <Switch checked={!!notifs[it.key]} onCheckedChange={() => toggle(it.key)} data-testid={`notif-toggle-${it.key}`} />
            </div>
          ))}
        </div>
        <div className="mt-5">
          <Button onClick={saveNotifs} disabled={savingNotifs} data-testid="save-notifs-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            {savingNotifs && <Loader2 size={15} className="mr-2 animate-spin" />} Save preferences
          </Button>
        </div>
      </Card>
      </div>
    </div>
  );
}
